/**
 * Shared end-of-session tracking. Provider-agnostic — reads session
 * meta and transcript via a `SessionDataAdapter`, persists both
 * through the same Powtoon BE / S2S endpoints LiveAvatar has always
 * used, and (for MCQ) kicks off the shared scoring job.
 *
 * Extracted from `SessionFrame.tsx` so `TavusFrame` (and eventually
 * D-ID) can reuse the exact same pipeline.
 */
import { useCallback, useEffect, useRef } from "react";
import { powtoonBE, powtoonBESessionBeacon } from "@/lib/powtoon-be/client";
import { powtoonS2S, powtoonS2SBeacon } from "@/lib/powtoon-s2s/client";
import {
  startScoringJob,
  pollScoringJob,
} from "@/lib/powtoon-s2s/scoringJobClient";
import type { Scenario, ScoreResult } from "@/lib/powtoon-be/types";
import type { SessionDataAdapter } from "@/lib/scenarios/providers/sessionAdapter";
import {
  interactionModeOf,
  questionnaireIdsOfContent,
  playedRolePlayScenarioIdOf,
} from "@/lib/scenarios/providers/spec";
import { createLogger } from "@/lib/frontend-logger";

const logger = createLogger("session-tracking");

type Options = {
  scenario: Scenario;
  publicLinkToken?: string;
  adapter: SessionDataAdapter;
};

export function useSessionTracking({ scenario, publicLinkToken, adapter }: Options) {
  const trackedRef = useRef(false);
  const transcriptObjectIdRef = useRef<string | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);
  const startedAtRef = useRef<Date | null>(null);
  const endedAtRef = useRef<Date | null>(null);
  // Which role-play scenario this session plays. Decided up front (never by
  // the avatar at runtime) so the BE can resolve the grading rubric.
  const rolePlayScenarioId =
    interactionModeOf(scenario.content) === "role_play"
      ? playedRolePlayScenarioIdOf(scenario.content)
      : undefined;

  useEffect(() => {
    return () => {
      pollAbortRef.current?.abort();
    };
  }, []);

  const markStarted = useCallback((date?: Date) => {
    if (!startedAtRef.current) startedAtRef.current = date ?? new Date();
  }, []);

  const markEnded = useCallback((date?: Date) => {
    endedAtRef.current = date ?? new Date();
  }, []);

  const reset = useCallback(() => {
    trackedRef.current = false;
    transcriptObjectIdRef.current = null;
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
    startedAtRef.current = null;
    endedAtRef.current = null;
  }, []);

  const trackOnEnd = useCallback(
    async (input: {
      sessionId: string | null | undefined;
      endedVia?: "user" | "unload";
    }): Promise<{ transcriptObjectId: string | null }> => {
      const sid = input.sessionId;
      if (!sid || trackedRef.current) {
        return { transcriptObjectId: transcriptObjectIdRef.current };
      }
      trackedRef.current = true;
      const endedAt = endedAtRef.current ?? new Date();
      endedAtRef.current = endedAt;
      const startedAt = startedAtRef.current;
      const clientDurationSeconds = startedAt
        ? Math.max(0, Math.round((endedAt.getTime() - startedAt.getTime()) / 1000))
        : null;

      // 1. Session meta.
      let sessionData: Record<string, unknown> | null = null;
      try {
        logger.info("session meta fetch started", { scenario_id: scenario.id, session_id: sid });
        sessionData = await adapter.fetchSessionMeta(sid);
        logger.info("session meta fetch completed", { scenario_id: scenario.id, session_id: sid });
      } catch (e) {
        console.warn("[session] meta fetch failed:", e);
        logger.warn("session meta fetch failed", {
          scenario_id: scenario.id,
          session_id: sid,
          error: (e as Error)?.message ?? String(e),
        });
      }
      const sessionContent = {
        session_id: sid,
        ...(sessionData ?? {}),
        ...(rolePlayScenarioId
          ? { roleplay_scenario_id: rolePlayScenarioId }
          : {}),
        client_started_at: startedAt?.toISOString() ?? null,
        client_ended_at: endedAt.toISOString(),
        client_duration_seconds: clientDurationSeconds,
        ...(input.endedVia === "unload" ? { ended_via: "unload" } : {}),
      };
      try {
        if (publicLinkToken) {
          logger.info("session save started", { scenario_id: scenario.id, session_id: sid, public: true });
          await powtoonS2S.trackPublicSession({
            scenarioId: scenario.id,
            publicLinkToken,
            content: sessionContent,
          });
        } else {
          logger.info("session save started", { scenario_id: scenario.id, session_id: sid, public: false });
          await powtoonBE.sessions.track(scenario.id, sessionContent);
        }
        logger.info("session save completed", { scenario_id: scenario.id, session_id: sid });
      } catch (e) {
        console.warn("[session] track-on-end failed:", e);
        logger.warn("session save failed", {
          scenario_id: scenario.id,
          session_id: sid,
          error: (e as Error)?.message ?? String(e),
        });
      }

      // 2. Transcript.
      let transcriptObjectId: string | null = null;
      try {
        logger.info("transcript fetch started", { scenario_id: scenario.id, session_id: sid });
        const transcript = await adapter.fetchTranscript(sid);
        const transcriptLength = transcript?.transcript_data?.length ?? 0;
        logger.info("transcript fetch completed", {
          scenario_id: scenario.id,
          session_id: sid,
          transcript_length: transcriptLength,
        });
        const transcriptContent = {
          session_id: sid,
          ...(rolePlayScenarioId
            ? { roleplay_scenario_id: rolePlayScenarioId }
            : {}),
          fetched_at: new Date().toISOString(),
          session_active: transcript?.session_active,
          next_timestamp: transcript?.next_timestamp,
          transcript: transcript?.transcript_data ?? [],
        };
        try {
          logger.info("transcript save started", { scenario_id: scenario.id, session_id: sid, public: !!publicLinkToken });
          const tracked = publicLinkToken
            ? await powtoonS2S.trackPublicTranscript({
                scenarioId: scenario.id,
                publicLinkToken,
                content: transcriptContent,
              })
            : await powtoonBE.sessions.trackTranscript(scenario.id, transcriptContent);
          transcriptObjectId = (tracked as { id?: string })?.id ?? null;
          logger.info("transcript save completed", {
            scenario_id: scenario.id,
            session_id: sid,
            transcript_object_id: transcriptObjectId,
          });
        } catch (e) {
          console.warn("[session] transcript save failed:", e);
          logger.warn("transcript save failed", {
            scenario_id: scenario.id,
            session_id: sid,
            error: (e as Error)?.message ?? String(e),
          });
        }
      } catch (e) {
        console.warn("[session] transcript fetch failed:", e);
        logger.warn("transcript fetch failed", {
          scenario_id: scenario.id,
          session_id: sid,
          error: (e as Error)?.message ?? String(e),
        });
      }
      transcriptObjectIdRef.current = transcriptObjectId;
      return { transcriptObjectId };
    },
    [adapter, publicLinkToken, scenario.id, rolePlayScenarioId],
  );

  const runScoringJob = useCallback(
    async (transcriptObjectId: string): Promise<{
      result: ScoreResult | null;
      error: string | null;
      aborted: boolean;
    }> => {
      pollAbortRef.current?.abort();
      const ctrl = new AbortController();
      pollAbortRef.current = ctrl;
      const provider = scenario.content?.provider;
      // Providers without a `context_block` keep mode + questionnaire versions
      // on their own content block, so the ids have to be passed explicitly to
      // the scoring job. The spec registry resolves the right location.
      const isLiveAvatar = (provider ?? "liveavatar") === "liveavatar";
      const blockQuestionnaireIds = isLiveAvatar
        ? undefined
        : questionnaireIdsOfContent(scenario.content).filter(
            (id): id is string => typeof id === "string" && id.length > 0,
          );
      const mode = interactionModeOf(scenario.content);
      // LiveAvatar questionnaires keep the historical payload shape (mode +
      // questionnaire ids resolved server-side); role play always needs the
      // mode so the BE applies the role-play rubric.
      const interactionMode =
        isLiveAvatar && mode !== "role_play" ? undefined : mode;
      try {
        logger.info("scoring pipeline started", {
          scenario_id: scenario.id,
          transcript_object_id: transcriptObjectId,
          provider,
          questionnaire_id: blockQuestionnaireIds?.[0] ?? null,
          roleplay_scenario_id: rolePlayScenarioId ?? null,
        });
        await startScoringJob({
          scenarioId: scenario.id,
          transcriptObjectId,
          publicLinkToken,
          provider,
          questionnaireIds: blockQuestionnaireIds,
          interactionMode,
          rolePlayScenarioId,
        });
        const result = await pollScoringJob({
          scenarioId: scenario.id,
          transcriptObjectId,
          publicLinkToken,
          signal: ctrl.signal,
        });
        if (ctrl.signal.aborted) return { result: null, error: null, aborted: true };
        logger.info("scoring pipeline completed", {
          scenario_id: scenario.id,
          transcript_object_id: transcriptObjectId,
          pass: result.pass,
          grade: result.grade ?? null,
        });
        return { result, error: null, aborted: false };
      } catch (err) {
        if (ctrl.signal.aborted) return { result: null, error: null, aborted: true };
        const raw = (err as Error)?.message ?? "";
        console.error("[session] scoring failed:", err);
        logger.error("scoring pipeline failed", {
          scenario_id: scenario.id,
          transcript_object_id: transcriptObjectId,
          provider,
          error: raw,
        });
        const friendly = /missing or empty content\.questions/i.test(raw)
          ? "This questionnaire isn't ready to be scored yet — the trainer hasn't added any questions. Please contact your trainer."
          : "We couldn't score your session right now. Please try again later or contact your trainer if the problem persists.";
        return { result: null, error: friendly, aborted: false };
      }
    },
    [publicLinkToken, scenario.id, scenario.content, rolePlayScenarioId],
  );

  const beaconOnUnload = useCallback(
    (sessionId: string | null | undefined) => {
      if (!sessionId || trackedRef.current) return;
      trackedRef.current = true;
      const startedAt = startedAtRef.current;
      const endedAt = new Date();
      const clientDurationSeconds = startedAt
        ? Math.max(0, Math.round((endedAt.getTime() - startedAt.getTime()) / 1000))
        : null;
      const content = {
        session_id: sessionId,
        ...(rolePlayScenarioId ? { roleplay_scenario_id: rolePlayScenarioId } : {}),
        client_started_at: startedAt?.toISOString() ?? null,
        client_ended_at: endedAt.toISOString(),
        client_duration_seconds: clientDurationSeconds,
        ended_via: "unload",
      };
      if (publicLinkToken) {
        powtoonS2SBeacon("track_session", {
          scenario_id: scenario.id,
          public_link_token: publicLinkToken,
          content,
        });
      } else {
        powtoonBESessionBeacon({ scenarioId: scenario.id, content });
      }
    },
    [publicLinkToken, scenario.id, rolePlayScenarioId],
  );

  return {
    markStarted,
    markEnded,
    reset,
    trackOnEnd,
    runScoringJob,
    beaconOnUnload,
    transcriptObjectIdRef,
  };
}